import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    overflow: hidden;
    font-family: 'Arial', sans-serif;
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
`;

const shine = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
`;

const Container = styled.div.attrs({
  className: 'birthday-container'
})`
  width: 100vw;
  height: 100vh;
  min-height: -webkit-fill-available;
  background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 50%, #ffd1ff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  padding: 20px;
  box-sizing: border-box;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, 
      rgba(255,255,255,0.2) 25%, 
      transparent 25%, 
      transparent 50%, 
      rgba(255,255,255,0.2) 50%, 
      rgba(255,255,255,0.2) 75%, 
      transparent 75%, 
      transparent);
    background-size: 100px 100px;
    animation: ${shine} 3s linear infinite;
    opacity: 0.4;
  }
`;

const Cake = styled.div.attrs({
  className: 'birthday-cake'
})`
  font-size: min(100px, 25vw);
  margin-bottom: min(20px, 4vw);
  animation: ${float} 4s ease-in-out infinite;
  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
  transform-origin: center;
  &:hover {
    transform: scale(1.1);
    transition: transform 0.3s ease;
  }
`;

const Balloon = styled.div.attrs({
  className: 'birthday-balloon'
})`
  position: absolute;
  font-size: min(50px, 10vw);
  animation: ${float} 4s ease-in-out infinite;
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.4));
  &:nth-child(1) { top: 10%; left: 10%; animation-delay: 0s; }
  &:nth-child(2) { top: 20%; right: 10%; animation-delay: 0.5s; }
  &:nth-child(3) { top: 30%; left: 20%; animation-delay: 1s; }
  &:nth-child(4) { top: 40%; right: 20%; animation-delay: 1.5s; }
  &:nth-child(5) { top: 50%; left: 30%; animation-delay: 2s; }
  &:nth-child(6) { top: 60%; right: 30%; animation-delay: 2.5s; }
  &:hover {
    transform: scale(1.2);
    transition: transform 0.3s ease;
  }
`;

const Title = styled.h1.attrs({
  className: 'birthday-title'
})`
  font-size: min(56px, 12vw);
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2),
               0 0 20px rgba(255, 255, 255, 0.5);
  margin-bottom: min(20px, 4vw);
  text-align: center;
  animation: ${float} 4s ease-in-out infinite;
  animation-delay: 0.5s;
  background: linear-gradient(90deg, #fff, #ffd700, #ff69b4, #fff);
  background-size: 300% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shine} 3s linear infinite;
  white-space: nowrap;
  letter-spacing: 2px;
  font-weight: bold;
`;

const Message = styled.p.attrs({
  className: 'birthday-message'
})`
  font-size: min(28px, 6vw);
  color: #fff;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2),
               0 0 10px rgba(255, 255, 255, 0.3);
  text-align: center;
  max-width: min(700px, 90vw);
  margin: 0 auto min(40px, 8vw);
  line-height: 1.8;
  background: rgba(255, 255, 255, 0.15);
  padding: min(25px, 5vw);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1),
              inset 0 0 20px rgba(255, 255, 255, 0.2);
  transform: translateY(0);
  transition: all 0.4s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15),
                inset 0 0 30px rgba(255, 255, 255, 0.3);
  }
`;

const Sparkle = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: white;
  animation: ${sparkle} 2s ease-in-out infinite;
  pointer-events: none;
`;

const firework = keyframes`
  0% { 
    transform: translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  100% { 
    transform: translate(var(--x), var(--y)) rotate(var(--rotate));
    opacity: 0;
  }
`;

const Particle = styled.div<{ x: number; y: number; color: string }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  width: 8px;
  height: 2px;
  background: ${props => props.color};
  box-shadow: 0 0 4px ${props => props.color};
  animation: ${firework} 3s cubic-bezier(0.1, 0.25, 0.1, 1) forwards;
  --x: ${() => (Math.random() - 0.5) * 300}px;
  --y: ${() => Math.random() * 400 + 100}px;
  --rotate: ${() => Math.random() * 360}deg;
  transform-origin: center;
  border-radius: 1px;
  will-change: transform, opacity;
  backface-visibility: hidden;
  perspective: 1000;
  transform-style: preserve-3d;
`;

const colors = [
  '#ff0000', // 红色
  '#ffd700', // 金色
  '#ff69b4', // 粉色
  '#87ceeb', // 天蓝色
  '#98fb98', // 浅绿色
  '#dda0dd', // 梅红色
  '#ffa07a', // 浅橙色
  '#ff4500', // 橙红色
  '#00ff00', // 亮绿色
  '#ff00ff', // 亮粉色
  '#ff1493', // 深粉色
  '#00bfff', // 深天蓝
  '#ff6347', // 番茄色
  '#7fffd4', // 碧绿色
  '#ff8c00', // 深橙色
];

interface ParticleType {
  id: number;
  x: number;
  y: number;
  color: string;
}

const sendBarkNotification = async () => {
  try {
    const response = await fetch('https://api.day.app/aMdhoBJn7cf86KrKp5NDHU/生日快乐！有人访问了你的生日祝福页面');
    const data = await response.json();
    console.log('Bark notification sent:', data);
  } catch (error) {
    console.error('Failed to send Bark notification:', error);
  }
};

export const HomePage: React.FC = () => {
  const [timeSince, setTimeSince] = useState({ days: 0 });
  const [particles, setParticles] = useState<ParticleType[]>([]);
  const [sparkles, setSparkles] = useState<Array<{ id: number; x: number; y: number }>>([]);
  
  // 使用 useMemo 来缓存 meetingDate
  const meetingDate = useMemo(() => new Date('2025-06-01'), []);

  const calculateTimeSince = useCallback((startDate: Date) => {
    const now = new Date();
    const diff = now.getTime() - startDate.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return { days: days + 1 };
  }, []);

  const createParticles = useCallback((x: number, y: number) => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: Date.now() + i,
      x,
      y,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
    
    setParticles(prev => [...prev, ...newParticles]);
    
    setTimeout(() => {
      setParticles(prev => prev.filter(p => !newParticles.find(np => np.id === p.id)));
    }, 3000);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    createParticles(e.clientX, e.clientY);
  }, [createParticles]);

  useEffect(() => {
    sendBarkNotification();
    
    const updateTime = () => {
      setTimeSince(calculateTimeSince(meetingDate));
    };
    
    updateTime();
    const timer = setInterval(updateTime, 1000);
    
    return () => clearInterval(timer);
  }, [meetingDate, calculateTimeSince]);

  useEffect(() => {
    const createSparkle = () => {
      const sparkle = {
        id: Date.now(),
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
      };
      setSparkles(prev => [...prev, sparkle]);
      setTimeout(() => {
        setSparkles(prev => prev.filter(s => s.id !== sparkle.id));
      }, 2000);
    };

    const interval = setInterval(createSparkle, 300);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <GlobalStyle />
      <Container onClick={handleClick}>
        <Balloon>🎈</Balloon>
        <Balloon>🎈</Balloon>
        <Balloon>🎈</Balloon>
        <Balloon>🎈</Balloon>
        <Balloon>🎈</Balloon>
        <Balloon>🎈</Balloon>

        <Cake>🎂</Cake>
        <Title>利利·生日快乐！</Title>
        <Message>
          今天是我们认识的第
          {timeSince.days}天
          <br />
          愿你的每一天都像今天一样特别<br />
          愿我们的故事继续书写<br />
          生日快乐！<br />
          爱你哦！
        </Message>

        {sparkles.map(sparkle => (
          <Sparkle
            key={sparkle.id}
            style={{
              left: sparkle.x,
              top: sparkle.y,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}

        {particles.map(particle => (
          <Particle
            key={particle.id}
            x={particle.x}
            y={particle.y}
            color={particle.color}
          />
        ))}
      </Container>
    </>
  );
}; 